import React, { useState, useEffect, useRef, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layoutBlack";
import { useSelector, useDispatch } from "react-redux";
import CheckoutForm from "../components/checkoutform";
import { config } from "../utils/constants";
import axios from "axios";
import { setCartData } from "../store/slices/cartSlice.ts";

const stripePromise = loadStripe(config.url.STRIPE_PUBLIC_KEY);

const Payment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const REACT_APP_API_URL = config.url.REACT_APP_API_URL;

  const cartItems = useSelector((state) => state.cart.items);
  const discountCode = useSelector((state) => state.cart.discount_code);
  const subtotal = useSelector((state) => state.cart.subtotal);
  const total = useSelector((state) => state.cart.total_price);
  const updated_rates_id = useSelector((state) => state.cart.rates_id);
  const shipping_price_paid = useSelector((state) => state.cart.shipping_price);
  const discount_value = useSelector((state) => state.cart.discount_value);

  const shippingInfo = useSelector((state) => state.cart.shipping_info);
  const billingInfo = useSelector((state) => state.cart.billing_info);
  const shippingRates = useSelector((state) => state.cart.shipping_rates || {});
  const carrier = useSelector((state) => state.cart.carrier);
  const service_level = useSelector((state) => state.cart.service_level);

  const [clientSecret, setClientSecret] = useState("");
  const [chargeAmount, setChargeAmount] = useState("");
  const [appliedPromotionCode, setAppliedPromotionCode] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const dataFetchedRef = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const selectedRate = useMemo(() => {
    if (!updated_rates_id) return null;
    return shippingRates?.[updated_rates_id] || null;
  }, [shippingRates, updated_rates_id]);

  useEffect(() => {
    const hasShippingInfo =
      shippingInfo &&
      shippingInfo.firstName &&
      shippingInfo.lastName &&
      shippingInfo.street1 &&
      shippingInfo.city &&
      shippingInfo.state &&
      shippingInfo.zip &&
      shippingInfo.country &&
      shippingInfo.email;

    const hasBillingInfo =
      billingInfo &&
      billingInfo.firstName &&
      billingInfo.lastName &&
      billingInfo.street1 &&
      billingInfo.city &&
      billingInfo.country &&
      billingInfo.email;

    console.log("Payment guard check:", {
      shippingInfo,
      billingInfo,
      updated_rates_id,
      shipping_price_paid,
      carrier,
      service_level,
    });

    if (!hasShippingInfo || !hasBillingInfo) {
      navigate("/shipping-info", { replace: true });
      return;
    }

    const missingRateSelection =
      !updated_rates_id ||
      shipping_price_paid === null ||
      shipping_price_paid === undefined ||
      !carrier ||
      !service_level;

    if (missingRateSelection) {
      navigate("/choose-carrier", { replace: true });
      return;
    }
  }, [
    navigate,
    shippingInfo,
    billingInfo,
    updated_rates_id,
    shipping_price_paid,
    carrier,
    service_level,
  ]);

useEffect(() => {
  const hasRequiredCheckoutData =
    shippingInfo &&
    shippingInfo.firstName &&
    shippingInfo.lastName &&
    shippingInfo.street1 &&
    shippingInfo.city &&
    shippingInfo.state &&
    shippingInfo.zip &&
    shippingInfo.country &&
    shippingInfo.email &&
    billingInfo &&
    billingInfo.email &&
    updated_rates_id;

  if (!hasRequiredCheckoutData) return;
  if (dataFetchedRef.current) return;

  dataFetchedRef.current = true;

  axios({
    method: "post",
    url: `${REACT_APP_API_URL}/create-payment-intent/`,

    data: {
      // Account/customer information
      email: shippingInfo.email,

      // Billing information
      billing_email: billingInfo.email,
      billing_firstname: billingInfo.firstName,
      billing_lastname: billingInfo.lastName,

      // Shipping recipient
      shipping_firstname: shippingInfo.firstName,
      shipping_lastname: shippingInfo.lastName,
      shipping_email: shippingInfo.email,
      shipping_phone: shippingInfo.phone || "",

      // Shipping address used by Stripe Tax
      shipping_address1: shippingInfo.street1,
      shipping_address2: shippingInfo.street2 || "",
      shipping_city: shippingInfo.city,
      shipping_state: shippingInfo.state,
      shipping_zipcode: shippingInfo.zip,
      shipping_country: shippingInfo.country,

      // Cart and shipping-rate information
      discount_code: discountCode,
      rates_id: updated_rates_id,
      shipping_price: shipping_price_paid,
      carrier: carrier,
      service_level: service_level,
    },
  })
    .then((res) => {
      setCheckoutError("");
      setClientSecret(res.data.clientSecret);
      setAppliedPromotionCode(
        res.data.appliedPromotionCode || ""
      );

      setChargeAmount(
        parseFloat(res.data.chargeAmount).toFixed(2)
      );

      dispatch(
        setCartData({
          items: res.data.cart.items,
          rates_id: res.data.cart.rates_id,
          subtotal: res.data.cart.subtotal,
          total_price: res.data.cart.total_price,
          discount_code: res.data.cart.discount_code,
          discount_amount: res.data.cart.discount_amount,
          discount_value: res.data.cart.discount_value,
          shipping_price:
            res.data.cart.shipping_price_paid,
          taxes: res.data.cart.taxes,
          carrier: res.data.cart.carrier,
          service_level: res.data.cart.service_level,
          box_length_inches:
            res.data.cart.box_length_inches,
          box_width_inches:
            res.data.cart.box_width_inches,
          box_height_inches:
            res.data.cart.box_height_inches,
          box_weight_ounces:
            res.data.cart.box_weight_ounces,
        })
      );
    })
    .catch((error) => {
      console.error(
        "Error creating payment intent:",
        error.response?.data || error
      );

      const errorCode = error.response?.data?.code || "";

      setCheckoutError(
        errorCode.startsWith("wholesale_")
          ? error.response.data.detail ||
              "This cart does not meet wholesale checkout requirements."
          : "We were unable to prepare your checkout. Please review your cart and try again."
      );

      dataFetchedRef.current = false;
    });
}, [
  REACT_APP_API_URL,
  billingInfo,
  carrier,
  discountCode,
  dispatch,
  service_level,
  shippingInfo,
  shipping_price_paid,
  updated_rates_id,
]);

  const appearance = {
    theme: "stripe",
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <Layout>
      <section className="full-page-section bg0 p-t-120 p-b-80">
        <div className="container">
          <div className="row isotope-grid">
            <section className="full-page-section bg0 py-5">
              <div className="container">
                <h2 className="mb-5 text-center">Secure Checkout</h2>

                {checkoutError && (
                  <div className="alert alert-danger" role="alert">
                    {checkoutError}
                    {" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline"
                      onClick={() => navigate("/cart")}
                    >
                      Return to cart
                    </button>
                  </div>
                )}

                <div className="row">
                  <div className="col-lg-6 mb-5">
                    <div className="border rounded shadow-sm p-4 bg-white">
                      <h4 className="mb-4">Payment Details</h4>

                      {clientSecret && shippingInfo && (
                        <Elements options={options} stripe={stripePromise}>
                          <CheckoutForm
                            charge_amount={parseFloat(chargeAmount || 0).toFixed(2)}
                            shipping_amount={parseFloat(shipping_price_paid || 0).toFixed(2)}
                            carrier={carrier || ""}
                            service_level={service_level || ""}
                            email={shippingInfo.email}
                            applied_promotion_code={appliedPromotionCode}
                            rates_id={updated_rates_id}
                          />
                        </Elements>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="border rounded shadow-sm p-4 bg-light mb-4">
                      <h5>Shipping Address</h5>
                      <p className="mb-1">
                        <strong>
                          {shippingInfo?.firstName} {shippingInfo?.lastName}
                        </strong>
                      </p>
                      <p className="mb-1">{shippingInfo?.street1}</p>
                      {shippingInfo?.street2 && (
                        <p className="mb-1">{shippingInfo.street2}</p>
                      )}
                      <p className="mb-1">
                        {shippingInfo?.city}, {shippingInfo?.state} {shippingInfo?.zip}
                      </p>
                      <p className="mb-1">{shippingInfo?.country}</p>
                      <p>{shippingInfo?.email}</p>
                    </div>

                    <div className="border rounded shadow-sm p-4 bg-light mb-4">
                      <h5>Shipping Method</h5>
                      <p className="mb-0">
                        {carrier || selectedRate?.provider || "—"} -{" "}
                        {service_level || selectedRate?.service_level || "—"}
                      </p>
                    </div>

                    <div className="border rounded shadow-sm p-4 bg-light">
                      <h5>Order Summary</h5>

                      <ul className="list-group mb-3">
                        {cartItems.map((item) => (
                          <li
                            className="list-group-item d-flex justify-content-between lh-sm"
                            key={item.id}
                          >
                            <div>
                              <h6 className="my-0">{item.name}</h6>
                              <small className="text-muted">Qty: {item.quantity}</small>
                            </div>
                            <span className="text-muted">
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                          </li>
                        ))}

                        <li className="list-group-item d-flex justify-content-between">
                          <span>Subtotal</span>
                          <strong>${parseFloat(subtotal || 0).toFixed(2)}</strong>
                        </li>

                        <li className="list-group-item d-flex justify-content-between">
                          <span>Discount</span>
                          <strong className="text-success">
                            -${parseFloat(discount_value || 0).toFixed(2)}
                          </strong>
                        </li>

                        <li className="list-group-item d-flex justify-content-between">
                          <span>Shipping</span>
                          <strong>${parseFloat(shipping_price_paid || 0).toFixed(2)}</strong>
                        </li>

                        <li className="list-group-item d-flex justify-content-between">
                          <span>Total</span>
                          <strong>${parseFloat(total || 0).toFixed(2)}</strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Payment;